/**
 * Central definition of all resource types used across the application.
 * This file serves as the single source of truth for resource type configurations.
 */

export type ResourceTypeValue =
  | "advisory"
  | "article"
  | "blog"
  | "study"
  | "news"
  | "press"
  | "report"
  | "tool"
  | "video"
  | "patent";

export type ResourceType = ResourceTypeValue | "all";

export interface ResourceTypeConfig {
  value: ResourceTypeValue;
  indexUrl: string;
  labels: {
    singular: string;
    plural: string;
  };
  showCategoryFilters?: boolean;
  showRSSLink?: boolean;
}

export const RESOURCE_TYPES: ResourceTypeConfig[] = [
  {
    value: "advisory",
    indexUrl: "advisories",
    labels: {
      singular: "Advisory",
      plural: "Advisories",
    },
  },
  {
    value: "article",
    indexUrl: "articles",
    showRSSLink: true,
    labels: {
      singular: "Article",
      plural: "Articles",
    },
  },
  {
    value: "blog",
    indexUrl: "blog",
    showRSSLink: true,
    labels: {
      singular: "Blog",
      plural: "Blogs",
    },
  },
  {
    value: "study",
    indexUrl: "case-studies",
    showCategoryFilters: false,
    labels: {
      singular: "Case Study",
      plural: "Case Studies",
    },
  },
  {
    value: "news",
    indexUrl: "news",
    labels: {
      singular: "News",
      plural: "News",
    },
  },
  {
    value: "press",
    indexUrl: "press",
    labels: {
      singular: "Press",
      plural: "Press",
    },
  },
  {
    value: "report",
    indexUrl: "reports",
    labels: {
      singular: "Report",
      plural: "Reports",
    },
  },
  {
    value: "tool",
    indexUrl: "tools",
    labels: {
      singular: "Tool",
      plural: "Tools",
    },
  },
  {
    value: "patent",
    indexUrl: "patents",
    labels: {
      singular: "Patent",
      plural: "Patents",
    },
  },
  {
    value: "video",
    indexUrl: "videos",
    labels: {
      singular: "Video",
      plural: "Videos & Webinars",
    },
  },
];

/** Resource types that can be unpublished CMS drafts. Advisories are synced from GitHub already published. */
export const EDITORIAL_RESOURCE_TYPES: ResourceTypeValue[] = RESOURCE_TYPES
  .map((type) => type.value)
  .filter((value) => value !== "advisory");

export const VALID_RESOURCE_TYPES: readonly ResourceTypeValue[] = [
  "advisory",
  "article",
  "blog",
  "news",
  "study",
  "press",
  "report",
  "tool",
  "video",
  "patent",
] as const;

/**
 * Resource `templateType` values that should use Open Graph `article`
 * (time-based / editorial). Tool pages stay `website` (product-style).
 */
const RESOURCE_TEMPLATES_WITH_ARTICLE_OG: ReadonlySet<ResourceTypeValue> =
  new Set([
    "advisory",
    "article",
    "blog",
    "news",
    "study",
    "press",
    "report",
    "video",
    "patent",
  ]);

export { SHARE_IMAGES, DEFAULT_SHARE_IMAGE } from "./seo/share-images";

export function openGraphTypeForResourceTemplate(
  templateType: string | null | undefined
): "article" | "website" {
  if (
    templateType &&
    RESOURCE_TEMPLATES_WITH_ARTICLE_OG.has(
      templateType as ResourceTypeValue
    )
  ) {
    return "article";
  }
  return "website";
}

/**
 * Get resource type configuration by value
 */
export function getResourceTypeConfig(
  value: ResourceTypeValue
): ResourceTypeConfig | undefined {
  return RESOURCE_TYPES.find((type) => type.value === value);
}
